import {getKeyedCopy, type KeyedCopyEntry} from "@repo/utils/keyedCopy";
import {parseDateOnly} from "@repo/shopify";
import {CLASS_SCHEDULE_COPY} from "../ClassScheduleCopyConstants";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Resolve the "starting soon" window in days from keyed copy, defaulting to 14.
 */
export function getStartingSoonThresholdDays(
	keyedCopy: KeyedCopyEntry[] = []
): number {
	// Get the value from keyed copy and parse it as an integer.
	const raw = getKeyedCopy(
		keyedCopy,
		CLASS_SCHEDULE_COPY.startingSoon.threshold
	);
	const parsed = Number.parseInt(raw, 10);

	// If a bad value is provided, return the fallback.
	if (!Number.isFinite(parsed) || parsed < 0) {
		return Number.parseInt(
			CLASS_SCHEDULE_COPY.startingSoon.threshold.fallback,
			10
		);
	}

	return parsed;
}

/**
 * True when `dateStartAt` is today or within the next `thresholdDays` days.
 */
export function isStartingSoon(
	dateStartAt: string | null | undefined,
	thresholdDays: number
): boolean {
	// Return false for a bad value
	if (!dateStartAt || thresholdDays < 0) {
		return false;
	}

	// Parse the date and return false for a bad value
	const start = parseDateOnly(dateStartAt);
	if (!start) {
		return false;
	}

	// Get today as a date
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	// Calculate the number of days until the start date
	const daysUntil = Math.round(
		(start.getTime() - today.getTime()) / MS_PER_DAY
	);

	// Return true if the start date is within the threshold days
	return daysUntil >= 0 && daysUntil <= thresholdDays;
}
